import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useGetDispatchHistory } from "../../../utils/react-query/queryDispatches/queries";
import { actionLabels, statusLabels } from "../statusLabels";
import type { DispatchStatus } from "../types";

interface DispatchHistoryModalProps {
  dispatchId: number | null;
  onClose: () => void;
}

function formatStatus(value: string | null) {
  if (!value) return "—";
  return statusLabels[value as DispatchStatus] ?? value;
}

function formatAction(action: string) {
  return actionLabels[action] ?? action;
}

function formatDateTime(isoString: string) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Audit-detail renderer for the history actions (plain text, reservations-modal
 * style).
 *
 * Observations by role (odd/tasks/dispatch-observations-by-role.md): every
 * stage records who observed what, so `created` and `edited` render their
 * observation too instead of falling through to "—".
 *
 *  - `created` carries it as `details.observation` (written by
 *    DispatchService.create).
 *  - `edited` carries it as `details.notes`, because the backend stores an
 *    edit as the applied patch and `notes` is the dispatch's observation
 *    column.
 */
export function renderDetails(
  details: Record<string, unknown> | null,
  action: string
): string {
  if (!details) return "—";

  switch (action) {
    case "approved": {
      const parts = [`Factura: ${asText(details.invoice_number) ?? "—"}`];
      const observation = asText(details.observation);
      if (observation) parts.push(observation);
      return parts.join(", ");
    }

    case "rejected":
      return asText(details.observation) ?? "—";

    case "created":
      return asText(details.observation) ?? "—";

    case "edited":
      return asText(details.notes) ?? "—";

    default:
      return "—";
  }
}

export function DispatchHistoryModal({
  dispatchId,
  onClose,
}: DispatchHistoryModalProps) {
  const { data, isLoading } = useGetDispatchHistory({
    dispatch_id: dispatchId ?? 0,
  });

  if (dispatchId == null) return null;

  const results = data?.results ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        disableTypography
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">
          Historial del despacho #{dispatchId}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem",
            }}
          >
            <CircularProgress size={32} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginLeft: 12, alignSelf: "center" }}
            >
              Cargando historial...
            </Typography>
          </div>
        )}

        {!isLoading && results.length === 0 && (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ padding: "2rem" }}
          >
            No hay historial registrado para este despacho.
          </Typography>
        )}

        {!isLoading && results.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Acción</strong>
                </TableCell>
                <TableCell>
                  <strong>Estado</strong>
                </TableCell>
                <TableCell>
                  <strong>Realizado por</strong>
                </TableCell>
                <TableCell>
                  <strong>Detalles</strong>
                </TableCell>
                <TableCell>
                  <strong>Fecha</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{formatAction(entry.action)}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {formatStatus(entry.status_from)} →{" "}
                    {formatStatus(entry.status_to)}
                  </TableCell>
                  <TableCell>
                    {entry.performed_by_name || (
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {renderDetails(entry.details, entry.action)}
                  </TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {formatDateTime(entry.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DispatchHistoryModal;
